import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { AuthService, RoutingService,ProductSearchPage } from '@spartacus/core'
import { CurrentProductService, TableService, TableStructure } from '@spartacus/storefront'
import { Observable } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { DEFAULT_TABLE_STRUCTURE } from '../../../../components/table/eb-table.interface'
import { EbProductVariantsTableDefaultComponent } from '../../components/product-variants-table/eb-product-variants-table-default.component'
import { EbProduct, EbProductSearchPage } from '../../interfaces/eb-product.interface'
import { EbProductService } from './eb-product.service'

@Injectable()
export class EbCurrentProductService extends CurrentProductService {
  constructor(
    routingService: RoutingService,
    protected localProductService: EbProductService,
    protected tableService: TableService,
    protected authService: AuthService,
    protected router: Router
  ) {
    super(routingService, localProductService)
  }

  private static featuresComparator(a, b) {
    if (a.position < b.position) {
      return -1
    } else if (a > b) {
      return 1
    } else {
      return 0
    }
  }

  getVariantsForTable(
    unitValue,
    pageSize = 10,
    page = 0,
    sorting = 'nameAsc',
    featureValues: { [key: string]: string } = {},
    freeTextSearch = ''
  ) {
    return this.getVariants(unitValue,pageSize, page, sorting, featureValues, freeTextSearch).pipe(
      map(variants => {
        variants.products.forEach(value => this.flattenClassifications(value as EbProduct))
        return variants
      })
    )
  }

  getVariants(
    unitValue,
    pageSize = 10,
    page = 0,
    sorting = 'nameAsc',
    featureValues: { [key: string]: string } = {},
    freeTextSearch = ''
  ): Observable<EbProductSearchPage> {
    return this.getProduct().pipe(
      switchMap(product => {
        const query = this.generateQuery(featureValues)
        return this.localProductService.getVariants(
          unitValue,
          product.code,
          pageSize,
          page,
          sorting,
          query,
          freeTextSearch
        )
      })
    )
  }

  enhanceCurrentRouteWithQuery(selectedValues) {
    const query = this.generateQuery(selectedValues)
    const urlTree = this.router.createUrlTree([], {
      queryParams: { query },
      queryParamsHandling: 'merge'
    })

    this.router.navigateByUrl(urlTree)
  }

  private flattenClassifications(product: EbProduct): void {
    product.classifications?.forEach(classification => {
      classification?.features?.forEach(feature => {
        product[feature.code.split('.')[2]] = feature?.featureValues
          .filter(featureValue => featureValue.value).map(value =>
          feature.featureUnit?.symbol !== undefined
            ? `${value.value} ${feature.featureUnit?.symbol}`
            : value.value
        )
      })
    })
  }

  private generateQuery(featureValues) {
    return Object.keys(featureValues)
      .filter(key => featureValues[key])
      .map(key => `:${key}:${featureValues[key].split('\xa0')[0]}`)
      .join('')
  }

  getVariantsTableStructure(variants: ProductSearchPage): Observable<TableStructure> {
    return this.tableService.buildStructure('variants', DEFAULT_TABLE_STRUCTURE).pipe(
      map(config => {
        const splicedConfig = { ...config, cells: [...config.cells] }
        this.buildUpDynamicTableConfigCells(variants).forEach(cell => {
          splicedConfig.options.cells[cell] = {
            dataComponent: EbProductVariantsTableDefaultComponent
          }
        })
        splicedConfig.cells.unshift(...this.buildUpDynamicTableConfigCells(variants))
        return splicedConfig
      })
    )
  }

  private buildUpDynamicTableConfigCells(variants: EbProductSearchPage): string[] {
    const cells = []
    variants.features.forEach(feature => {
      cells.push(feature)
    })
    cells.sort(EbCurrentProductService.featuresComparator)
    return cells
      .map(value => value.code.split('.')[2])
      .filter((value, index, self) => self.indexOf(value) === index)
      .slice(0, 4)
  }
}
